import React, { PureComponent, Component, PropTypes } from 'react';
import SessionSelector from '../../../components/SessionSelector';
import EmptyState from '../../../common/components/EmptyState';
import _ from 'underscore';
import cx from 'classnames';
import PageTitle from '../../../PageTitle';
import { EventScheduled } from '../../../components/event';
import moment from 'moment';
import { PageHeader, Table } from 'react-fast-ui';
import { NOTIFICATION_CONTEXT_TYPES } from '../../../NotificationSystemProvider';
import { getCurrentSession } from '../../../util/SessionUtils';
import ConfirmButton from '../../../components/buttons/ConfirmButton';
import { replace } from '../../../util/Etc';

class OutlierGameTable extends PureComponent {
  static propTypes = {
    onCancelGame: PropTypes.func.isRequired,
    games: PropTypes.arrayOf(PropTypes.object).isRequired
  };

  render() {
    const { games, onCancelGame } = this.props;

    return (
      <Table>
        <Table.thead>
          <Table.th>ID</Table.th>
          <Table.th>Scheduled</Table.th>
          <Table.th>Name</Table.th>
          <Table.th>Division</Table.th>
          <Table.th>Team 1</Table.th>
          <Table.th>Team 2</Table.th>
          <Table.th>Last Scraped</Table.th>
          <Table.th key="cancel"/>
        </Table.thead>
        <Table.tbody>
          {
            games.map((game) => {
              const { id, name, gameGroup, lastScraped, homeTeam, visitorTeam, homeSeed, visitorSeed } = game;
              const { division } = gameGroup;

              const hasTeam = !!(homeTeam || visitorTeam);

              return (
                <Table.tr className={cx('align-items-center', { 'opacity-40': !hasTeam })} key={id}>
                  <Table.td key="id">
                    {id}
                  </Table.td>
                  <Table.td key="scheduled">
                    <EventScheduled event={game}/>
                  </Table.td>
                  <Table.td key="name">
                    {name}
                  </Table.td>
                  <Table.td key="division">
                    {division.name}
                  </Table.td>
                  <Table.td key="t1">
                    {(homeTeam && homeTeam.name) || (homeSeed ? homeSeed.displayName : 'TBD')}
                  </Table.td>
                  <Table.td key="t2">
                    {(visitorTeam && visitorTeam.name) || (visitorSeed ? visitorSeed.displayName : 'TBD')}
                  </Table.td>
                  <Table.td key="ls">
                    {lastScraped === null ? 'Never' : moment(lastScraped).format('l LT')}
                  </Table.td>
                  <Table.td key="cancel">
                    <ConfirmButton
                      text="Cancel"
                      type="danger"
                      icon="ban"
                      disabled={!hasTeam}
                      size="sm"
                      onConfirm={() => onCancelGame(game)}
                    />
                  </Table.td>
                </Table.tr>
              );
            })
          }
        </Table.tbody>
      </Table>
    );
  }
}

/**
 * Page of games that have not been scraped recently
 */
export default class OutlierGamesPage extends Component {
  static contextTypes = {
    dao: PropTypes.object.isRequired,
    ...NOTIFICATION_CONTEXT_TYPES
  };

  state = {
    loadingGames: null,
    games: [],
    sessions: [],
    session: null
  };

  componentDidMount() {
    this.loadSessions();
  }

  loadSessions() {
    const { dao, onError } = this.context;
    dao.sessions.getAll({
      sort: [ 'A|start', 'A|end' ]
    }).then(
      sessions => this.setState({ sessions }, this.selectCurrentSession)
    ).catch(onError);
  }

  selectCurrentSession = () => this.handleSessionSelect(getCurrentSession(this.state.sessions));

  loadGames() {
    const { loadingGames, session } = this.state;
    const { dao, onError } = this.context;

    if (loadingGames) {
      loadingGames.cancel();
    }

    if (!session) {
      return;
    }

    this.setState({
      loadingGames: dao.games.getAll({
        recentlyScraped: false,
        // from the past hour
        fromTime: moment().valueOf() - (60 * 60 * 1000),
        sessionId: session.id,
        sort: [ 'A|scheduled' ]
      })
        .then(games => {
          // remove any games that are not in FILE tourneys
          return _.filter(games, (game) => {
            const { gameGroup } = game;
            const { division } = gameGroup;
            const { tourney } = division;

            return tourney.custom === false && tourney.sourceType !== 'FILE';
          });
        })
        .then(games => this.setState({ games }))
        .catch(onError)
        .finally(
          () => this.setState({ loadingGames: null })
        )
    });
  }

  handleSessionSelect = session => {
    this.setState({ session }, () => this.loadGames());
  };

  handleCancelGame = game => {
    const { dao, onError, onSuccess } = this.context;

    dao.games.save({ ...game, cancelled: true, sides: _.mapObject(game.sides, side => ({ ...side, team: null })) })
      .then(
        () => {
          onSuccess('Game has been cancelled. Notifications will be sent.');

          // refresh the game data for the cancelled game
          return dao.games.getSingle(game.id);
        }
      )
      .then(
        game => {
          const { games } = this.state;

          this.setState({
            games: replace(games, game)
          });
        }
      )
      .catch(onError);
  };

  renderGames() {
    const { games, loadingGames, sessions } = this.state;

    if (loadingGames || sessions.length === 0) {
      return EmptyState.LOADING;
    }

    if (games.length === 0) {
      return (
        <EmptyState>
          All games have been recently scraped in this session, or are in the past
        </EmptyState>
      );
    }

    // map of tourney ID to tourney object
    const tourneys = {};

    // get all the tourneys out of the games
    _.each(games, (game) => tourneys[ game.gameGroup.division.tourney.id ] = game.gameGroup.division.tourney);

    // index the games by tourney
    const byTourneyId = _.groupBy(games, (game) => game.gameGroup.division.tourney.id);

    const tables = [];

    _.each(byTourneyId, (gamesAtTourney, tourneyId) => {
      const tourney = tourneys[ tourneyId ];

      tables.push(
        <div key={tourneyId} className="mg-bottom-md pd-sm mg-sm box-shadow-sm">
          <h4 key="header" className="pd-sm">
            {tourney.name}
            <em className="mg-left-sm">(Last Scraped: {moment(tourney.maxLastScraped).format('l LT')})</em>
            <small className="mg-left-sm">
              {moment(tourney.start).format('l')} - {moment(tourney.end).format('l')}
            </small>
          </h4>
          <hr key="hr"/>
          <OutlierGameTable games={gamesAtTourney} onCancelGame={this.handleCancelGame}/>
        </div>
      );
    });

    return (
      <div>
        {tables}
      </div>
    );
  }

  render() {
    const { sessions, session } = this.state;

    return (
      <PageTitle title="Outlier Games">
        <div className="mg-bottom-lg">
          <PageHeader key="head" title="Outlier Games (Not Recently Scraped)"/>
          <PageHeader.Subhead>
            <SessionSelector sessions={sessions} value={session} onChange={this.handleSessionSelect}/>
          </PageHeader.Subhead>
          <div className="max-width" key="games">
            {this.renderGames()}
          </div>
        </div>
      </PageTitle>
    );
  }
}


// WEBPACK FOOTER //
// ./src/js/GameDay/components/pages/OutlierGamesPage.jsx